import { useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  Brain, LayoutDashboard, BookOpen, Users, Award, Newspaper, BarChart3,
  LogOut, Menu, X, ChevronRight, Settings, Globe, ShoppingBag, Receipt, MessageSquare, Code, Cpu, Rocket, Megaphone, Terminal, GraduationCap, Calculator
} from "lucide-react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const NAV_ITEMS = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
  { icon: BookOpen, label: "Trainings", path: "/admin/trainings" },
  { icon: ShoppingBag, label: "Documents", path: "/admin/books" },
  { icon: Receipt, label: "Document Purchases", path: "/admin/book-purchases" },
  { icon: Code, label: "Website Projects", path: "/admin/website-projects" },
  { icon: Calculator, label: "Quote Requests", path: "/admin/quotes" },
  { icon: Users, label: "Participants", path: "/admin/participants" },
  { icon: Award, label: "Certificates", path: "/admin/certificates" },
  { icon: MessageSquare, label: "Testimonials", path: "/admin/testimonials" },
  { icon: Newspaper, label: "News", path: "/admin/news" },
  { icon: BarChart3, label: "Analytics", path: "/admin/analytics" },
  { icon: Globe, label: "Website Traffic", path: "/admin/website-analytics" },
  { icon: Cpu, label: "AI Tools", path: "/admin/ai-tools" },
  { icon: Terminal, label: "Python Lab", path: "/admin/python-lab" },
  { icon: Rocket, label: "AI Founder", path: "/admin/ai-founder" },
  { icon: Megaphone, label: "Announcements", path: "/admin/announcements" },
  { icon: MessageSquare, label: "Popups", path: "/admin/popups" },
  { icon: GraduationCap, label: "ShansoLearn", path: "/admin/shanso-learn" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");

  const adminEmail = localStorage.getItem("admin_email") || "Admin";

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/admin/login");
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwError("");
    setPwSuccess("");
    if (pwForm.new_password !== pwForm.confirm) {
      setPwError("New passwords don't match");
      return;
    }
    if (pwForm.new_password.length < 6) {
      setPwError("Password must be at least 6 characters");
      return;
    }
    setPwLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/auth/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPwSuccess("Password changed successfully!");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      setTimeout(() => setShowPasswordModal(false), 1500);
    } catch (err) {
      setPwError(err.response?.data?.detail || "Failed to change password");
    } finally {
      setPwLoading(false);
    }
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-gray-200">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-black flex items-center justify-center rounded-sm">
            <Brain size={17} className="text-white" />
          </div>
          <span className="font-outfit font-bold text-gray-900 text-base">ShansoAI</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {NAV_ITEMS.map(item => {
          const active = location.pathname === item.path || (item.path !== "/admin" && location.pathname.startsWith(item.path));
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium font-dm-sans transition-all duration-150 group ${
                active
                  ? "bg-black text-white"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              }`}
            >
              <item.icon size={17} className={active ? "text-white" : "text-gray-400 group-hover:text-gray-600"} />
              {item.label}
              {active && <ChevronRight size={14} className="ml-auto opacity-60" />}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="px-3 py-4 border-t border-gray-200 space-y-0.5">
        <button
          data-testid="settings-btn"
          onClick={() => setShowPasswordModal(true)}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium font-dm-sans text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
        >
          <Settings size={17} className="text-gray-400" />
          Change Password
        </button>
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium font-dm-sans text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut size={17} className="text-gray-400" />
          Logout
        </button>
        <div className="px-3 pt-2">
          <p className="text-xs text-gray-400 font-dm-sans truncate">{adminEmail}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#F4F4F5] font-dm-sans">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-60 bg-white border-r border-gray-200 flex-col flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-60 bg-white border-r border-gray-200 flex flex-col z-50">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center px-6 flex-shrink-0">
          <button className="lg:hidden mr-4 text-gray-500 hover:text-gray-900" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="flex-1">
            <span className="text-sm text-gray-500 font-dm-sans">
              {NAV_ITEMS.find(n => location.pathname === n.path || (n.path !== "/admin" && location.pathname.startsWith(n.path)))?.label || "Dashboard"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold font-outfit">
                {adminEmail.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-outfit text-gray-900">Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordChange} className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Current Password</label>
              <input
                data-testid="current-password-input"
                type="password"
                value={pwForm.current_password}
                onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))}
                className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">New Password</label>
              <input
                data-testid="new-password-input"
                type="password"
                value={pwForm.new_password}
                onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))}
                className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-dm-sans">Confirm New Password</label>
              <input
                data-testid="confirm-password-input"
                type="password"
                value={pwForm.confirm}
                onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
                className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
                required
              />
            </div>
            {pwError && <p className="text-red-500 text-sm font-dm-sans">{pwError}</p>}
            {pwSuccess && <p className="text-green-600 text-sm font-dm-sans">{pwSuccess}</p>}
            <button
              data-testid="save-password-btn"
              type="submit"
              disabled={pwLoading}
              className="w-full bg-black text-white font-outfit font-bold py-2.5 hover:bg-gray-800 transition-colors disabled:opacity-60"
            >
              {pwLoading ? "Saving..." : "Save Password"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
